/**
 * Permanent visual-regression scenes for the export pipeline.
 *
 * Each scene is a canvas page (data only — no editor state) that exercises
 * one renderer branch end-to-end. `runExportRegression(captureFn)` mounts
 * each scene off-screen, captures it via the supplied captureFn (the same
 * one used by the live editor), and either compares to a stored signature
 * or returns the captured signatures for inspection.
 *
 * Signature = pHash-style coarse fingerprint of the exported canvas.
 * Independent of html2canvas's internal text-layout noise within a small
 * window — drift outside that window flags a real renderer regression.
 *
 * Intended caller: a console invocation in dev,
 *     `await window.__exportRunRegression()`
 * which logs `[export-regression]` per scene.
 */

export const REGRESSION_SCENES = [
  {
    id: 'multiline-typography',
    description: 'Multi-line wrapped text, mixed alignment, mixed weight',
    canvasSize: { width: 800, height: 400 },
    background: '#ffffff',
    elements: [
      { id: 't-headline', type: 'text', x: 40, y: 40, width: 720, height: 80,
        text: 'WHERE DREAMS', fontFamily: 'Helvetica, Arial, sans-serif',
        fontSize: 48, fontWeight: 800, color: '#1f1f1f', letterSpacing: 2, textAlign: 'left' },
      { id: 't-body', type: 'text', x: 40, y: 140, width: 360, height: 220,
        text: 'Thoughtfully designed homes with luxury finishes, modern amenities and unmatched comfort.',
        fontFamily: 'Helvetica, Arial, sans-serif', fontSize: 18, color: '#1f1f1f',
        lineHeight: 1.5, textAlign: 'left' },
      { id: 't-center', type: 'text', x: 440, y: 140, width: 320, height: 220,
        text: 'Center\naligned\nthree lines', fontFamily: 'Helvetica, Arial, sans-serif',
        fontSize: 24, fontWeight: 600, color: '#b08a4c', textAlign: 'center', lineHeight: 1.3 },
    ],
  },
  {
    id: 'rotated-masks',
    description: 'Rotated rect mask + rotated polygon mask on images',
    canvasSize: { width: 600, height: 400 },
    background: '#f5f5f5',
    elements: [
      { id: 'm-rect', type: 'shape', shape: 'rect', x: 60, y: 60, width: 200, height: 200,
        cornerRadius: 24, rotation: 18, fill: '#1f1f1f', isMaskShape: true },
      { id: 'i-rect', type: 'image', x: 60, y: 60, width: 200, height: 200,
        rotation: 18, clippedTo: 'm-rect', src: '/uploads/images/placeholder-house.jpg' },
      { id: 'm-poly', type: 'shape', shape: 'hexagon', x: 320, y: 60, width: 220, height: 220,
        rotation: 30, fill: '#b08a4c', isMaskShape: true },
      { id: 'i-poly', type: 'image', x: 320, y: 60, width: 220, height: 220,
        rotation: 30, clippedTo: 'm-poly', src: '/uploads/images/placeholder-house.jpg' },
    ],
  },
  {
    id: 'gradient-text',
    description: 'Linear-gradient fill + stroke-gradient text',
    canvasSize: { width: 800, height: 300 },
    background: '#0f0f12',
    elements: [
      { id: 't-fillgrad', type: 'text', x: 40, y: 40, width: 720, height: 100,
        text: 'GRADIENT FILL', fontFamily: 'Helvetica, Arial, sans-serif',
        fontSize: 72, fontWeight: 800, letterSpacing: 4, textAlign: 'center',
        fillGradient: { type: 'linear', angle: 45, colors: [
          { offset: 0,   color: '#b08a4c' },
          { offset: 1,   color: '#f6e1b2' },
        ]} },
      { id: 't-strokegrad', type: 'text', x: 40, y: 160, width: 720, height: 100,
        text: 'STROKE GRAD', fontFamily: 'Helvetica, Arial, sans-serif',
        fontSize: 72, fontWeight: 800, letterSpacing: 4, textAlign: 'center', color: 'transparent',
        strokeWidth: 2,
        strokeGradient: { type: 'linear', angle: 90, colors: [
          { offset: 0, color: '#ffffff' },
          { offset: 1, color: '#b08a4c' },
        ]} },
    ],
  },
  {
    id: 'nested-transforms',
    description: 'Rotated container with rotated child shapes',
    canvasSize: { width: 600, height: 600 },
    background: '#ffffff',
    elements: [
      { id: 's-bg', type: 'shape', shape: 'roundedRect', x: 100, y: 100, width: 400, height: 400,
        cornerRadius: 40, rotation: 12, fill: '#1f1f1f' },
      { id: 's-tri', type: 'shape', shape: 'triangle', x: 200, y: 200, width: 200, height: 200,
        rotation: -24, fill: '#b08a4c' },
      { id: 't-label', type: 'text', x: 150, y: 480, width: 300, height: 60,
        text: 'ROTATED LABEL', fontFamily: 'Helvetica, Arial, sans-serif',
        fontSize: 20, fontWeight: 700, color: '#1f1f1f', textAlign: 'center',
        rotation: -8, letterSpacing: 3 },
    ],
  },
  {
    id: 'clipped-images-and-qr',
    description: 'Image inside ellipse mask + QR code with bgColor',
    canvasSize: { width: 800, height: 400 },
    background: '#f5f5f5',
    elements: [
      { id: 'm-ellipse', type: 'shape', shape: 'circle', x: 40, y: 40, width: 320, height: 320,
        fill: '#1f1f1f', isMaskShape: true },
      { id: 'i-clipped', type: 'image', x: 40, y: 40, width: 320, height: 320,
        clippedTo: 'm-ellipse', src: '/uploads/images/placeholder-house.jpg' },
      { id: 'q-qr', type: 'qrcode', x: 500, y: 80, width: 240, height: 240,
        data: 'https://example.com/parity-test', color: '#1f1f1f', bgColor: '#ffffff' },
    ],
  },
];

// Coarse fingerprint of an HTMLCanvasElement — average luminance per
// 16×16 grid cell. Two captures of the same scene under different runs
// produce signatures within ≈1–2 units per cell from AA noise; renderer
// regressions move multiple cells by 10+ units. Cheap, stable, no
// dependencies.
export function fingerprintCanvas(exportedCanvas, gridSize = 16) {
  if (!exportedCanvas) return null;
  const { width: w, height: h } = exportedCanvas;
  const ctx = exportedCanvas.getContext('2d');
  if (!ctx || !w || !h) return null;
  const data = ctx.getImageData(0, 0, w, h).data;
  const cellW = Math.floor(w / gridSize);
  const cellH = Math.floor(h / gridSize);
  const out = new Array(gridSize * gridSize).fill(0);
  const cellPixels = cellW * cellH;
  if (cellPixels === 0) return null;
  for (let gy = 0; gy < gridSize; gy++) {
    for (let gx = 0; gx < gridSize; gx++) {
      let sum = 0;
      for (let y = gy * cellH; y < (gy + 1) * cellH; y++) {
        for (let x = gx * cellW; x < (gx + 1) * cellW; x++) {
          const idx = (y * w + x) * 4;
          sum += (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
        }
      }
      out[gy * gridSize + gx] = Math.round(sum / cellPixels);
    }
  }
  return out;
}

export function fingerprintDistance(a, b) {
  if (!a || !b || a.length !== b.length) return Infinity;
  let sum = 0;
  for (let i = 0; i < a.length; i++) sum += Math.abs(a[i] - b[i]);
  return sum / a.length;
}

/**
 * Golden fingerprints — populate via diagnostics output, then compare
 * future runs against these. Empty by default; a first run logs each
 * scene's fingerprint under `[export-regression]` so the maintainer
 * can paste in goldens. Distance threshold: cells average diff < 4 is
 * normal AA noise; > 8 is a regression.
 */
export const REGRESSION_GOLDENS = {
  // 'multiline-typography':   [/* 256 values */],
  // 'rotated-masks':          [/* 256 values */],
  // 'gradient-text':          [/* 256 values */],
  // 'nested-transforms':      [/* 256 values */],
  // 'clipped-images-and-qr':  [/* 256 values */],
};

export const REGRESSION_THRESHOLD = 8;

/**
 * Iterate every scene through the supplied scene-capture function.
 * `captureScene(scene) -> HTMLCanvasElement` must mount the scene's
 * elements/canvasSize/background into the editor and return the export
 * canvas. Returns an array of per-scene results with diff vs. golden.
 *
 * Intended invocation pattern:
 *   window.__exportRunRegression = () =>
 *     runExportRegression(async (scene) => { … mount & capture …; });
 *   await window.__exportRunRegression();
 */
export async function runExportRegression(captureScene) {
  const results = [];
  for (const scene of REGRESSION_SCENES) {
    const t0 = Date.now();
    let fingerprint = null, error = null;
    try {
      const canvas = await captureScene(scene);
      fingerprint = fingerprintCanvas(canvas);
    } catch (e) {
      error = String(e?.message || e);
    }
    const golden = REGRESSION_GOLDENS[scene.id] || null;
    const distance = golden && fingerprint ? fingerprintDistance(fingerprint, golden) : null;
    const regressed = distance !== null && distance > REGRESSION_THRESHOLD;
    const r = {
      id: scene.id,
      description: scene.description,
      ms: Date.now() - t0,
      fingerprint,
      hasGolden: !!golden,
      distance,
      regressed,
      error,
    };
    results.push(r);
    // eslint-disable-next-line no-console
    console.log('[export-regression]', r);
  }
  return results;
}
